import React, { useState, useMemo } from 'react';
import { Edit, Loading, useQuery } from 'react-admin';
import { makeStyles } from '@material-ui/core';
import { FormSpy } from 'react-final-form';
import { get, omit } from 'lodash';

import NavigationPrompt from '../../core/components/NavigationPrompt';
import EditToolbar from '../../core/components/edit/EditToolbar';
import FormTitleBar from '../../core/components/FormTitleBar';
import { removePathEnd, getInitials } from '../../core/utils';
import MailTemplateSummary from './MailTemplateSummary';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import { useFetch } from '../../core/hooks/UseFetch';
import DocumentCategoryApi from '../../core/apis/DocumentCategoryApi';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';

const useStyles = makeStyles({
  noActions: {
    marginTop: '0px'
  }
});

const MailTemplateTitle = ({ record }) => {
  return (
    <FormTitleBar
      name={record.description}
      showAvatar
      avatarContent={getInitials(record.description)}
    />
  );
};

const MailTemplateEdit = props => {
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const classes = useStyles();
  const { id } = props;

  const { data = [], loading: loadingCategory } = useFetch(
    DocumentCategoryApi.getCategoriesByTemplateID,
    id
  );

  const {
    data: distributionLists = [],
    loading: loadingDistributionLists
  } = useQuery({
    type: 'getList',
    resource: 'distributionlists',
    payload: {
      filter: {
        emailTemplateId: id
      }
    }
  });

  const {
    data: welcomeEmailConfig,
    loading: loadingWelcomeEmailConfig
  } = useQuery({
    type: 'getList',
    resource: 'tenantconfigurations',
    payload: {
      filter: {
        nameOrCategory: 'Welcome-Email-TemplateId'
      }
    }
  });

  const {
    data: newMupClientEmailConfig,
    loading: loadingNewMupClientEmailConfig
  } = useQuery({
    type: 'getList',
    resource: 'tenantconfigurations',
    payload: {
      filter: {
        nameOrCategory: 'NEW-MUP-Client-Email-TemplateId'
      }
    }
  });

  const isUsedForWelcomeEmail = useMemo(() => {
    return get(welcomeEmailConfig, '[0].value', '') === id;
  }, [welcomeEmailConfig, id]);

  const isUsedForNewMupClientEmail = useMemo(() => {
    return get(newMupClientEmailConfig, '[0].value', '') === id;
  }, [newMupClientEmailConfig, id]);

  if (
    loadingCategory ||
    loadingDistributionLists ||
    loadingWelcomeEmailConfig ||
    loadingNewMupClientEmailConfig
  )
    return <Loading />;

  const isLinkedToCategoryWithTriggerEmailOn = !!data.pageData.find(
    category => !!category.triggerEmail
  );
  const linkedToCategory = data.pageData.length > 0;
  const usedForAutomatedEmail =
    isLinkedToCategoryWithTriggerEmailOn ||
    isUsedForWelcomeEmail ||
    isUsedForNewMupClientEmail;

  // Check if Email Template Linked to any Distribution List then disable deletion
  const emailTemplateLinkedtoDL = distributionLists.length > 0;

  const handleFormIsDirty = dirty => {
    // Block navigation when the form is dirty
    setWhenBlockNavigation(dirty);
  };

  const onBeforeDelete = () => {
    setWhenBlockNavigation(false);
  };

  const {
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Edit
        {...omit(rest, ['userProfile'])}
        record={{
          description: ''
        }}
        undoable={false}
        title={<MailTemplateTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <CustomSimpleForm
          toolbar={
            <EditToolbar
              {...rest}
              hasEditPermission={hasEditPermission}
              hasDeletePermission={
                hasDeletePermission &&
                !usedForAutomatedEmail &&
                !emailTemplateLinkedtoDL &&
                !linkedToCategory
              }
              onBeforeDelete={onBeforeDelete}
            />
          }
          variant="standard"
          margin="normal"
        >
          <MailTemplateSummary usedForAutomatedEmail={usedForAutomatedEmail} />
          <FormSpy
            subscription={{ dirty: true }}
            onChange={props => {
              setTimeout(() => handleFormIsDirty(props.dirty), 1);
            }}
          />
        </CustomSimpleForm>
      </Edit>
    </React.Fragment>
  );
};

export default MailTemplateEdit;
